// Registre des solutions — le mecanisme d'inscription de FR-005 (spec 133).
//
// LE PROBLEME QU'IL RESOUT. Avant le 2026-09-07, `main.tsx` importait chaque page FinOps et
// declarait chaque route FinOps, et `navigation.ts` portait la section « FinOps 365 » en dur.
// Ajouter une solution demandait donc de modifier des fichiers du socle — ce que FR-006 interdit,
// et ce que le controle C2 refuse desormais.
//
// LE SENS DE LA DEPENDANCE EST INVERSE. Le socle n'importe aucune solution : il expose
// `registerSolution`, et chaque solution s'inscrit depuis SON propre module `register.tsx`.
// La decouverte se fait par MOTIF (`solutionsLoader.ts`), jamais par nom : le socle ne sait pas
// quelles solutions existent, seulement qu'il faut charger ce qui se trouve la.
//
// C'est la distinction de `data-model.md` rendue executable : le socle connait LE CONCEPT de
// solution (il en agrege les sections et les routes), il ne connait AUCUNE solution.

import { Fragment, createElement, type ComponentType, type ReactElement, type ReactNode } from 'react'
import type { NavItem, NavSection } from './navigation'
import type { PointDeVue } from './pointDeVue'

/**
 * Spec 161 (D-161-02, contracts/tableau-de-bord-et-menu.md § 1) — ce que la plateforme passe à la section qu'une
 * solution affiche sur le Tableau de bord. Le compte et la Vue client viennent du shell : la section ne résout jamais
 * son propre client (garde-fou B3). La section est montée une fois par client : changer de point de vue ne la démonte
 * pas, son état interne survit à la bascule.
 */
export interface SectionTableauDeBordProps {
  pointDeVue: PointDeVue
  accountId: string
  clientViewMode: boolean
}

/**
 * Spec 161, second retour de Christophe sur `dev` (2026-10-12) — actions qu'une solution propose dans le bandeau, en
 * menu sur la date du dernier audit. La plateforme ne sait ni ce qu'elles font ni pour quelle solution : elle les liste.
 */
export interface ActionBandeau {
  id: string
  libelle: string
  onSelect: () => void
  desactivee?: boolean
}

/** Ce que la plateforme passe au crochet de la solution : le client AFFICHÉ (celui de la coquille, spec 160). */
export interface ActionsBandeauParams {
  accountId: string | null
  /** Nom du client affiché, pour la confirmation qui le nomme. */
  nomClient: string
  /**
   * Faux pour un profil qui n'a pas ces gestes (Client), en Vue client, ou sans client affiché : la solution ne doit
   * alors proposer aucune action. La plateforme ne montre de toute façon aucun menu tant que `enabled` est faux.
   */
  enabled: boolean
}

export interface ActionsBandeau {
  items: ActionBandeau[]
  /**
   * Ce que les actions ouvrent (confirmation, volet de suivi, avis de réussite ou d'échec), rendu par la plateforme HORS
   * du menu déroulant, qui se démonte à la sélection — et hors de la barre de 56 px : en portail.
   */
  element: ReactNode
}

export interface SolutionRoute {
  /** Chemin RELATIF au shell authentifie (sans `/` initial), comme les routes du socle. */
  path: string
  element: ReactElement
}

/** Spec 150 (D-150-12) — onglet qu'une solution ajoute a la fiche client ; il recoit le compte affiche. */
export interface SolutionClientTab {
  id: string
  label: string
  render: (accountId: string) => ReactElement
}

/** Spec 150 (D-150-12) — onglet qu'une solution ajoute au hub « Clients » de l'administration (vue multi-clients). */
export interface SolutionClientsHubTab {
  id: string
  label: string
  element: ReactElement
}

export interface SolutionRegistration {
  /** Code de la solution, tel qu'il figure dans `portal_solutions` (ex. `finops-m365`). */
  code: string
  /** Section de navigation laterale. Son `solutionCode` doit valoir `code`. */
  navSection: NavSection
  /** Routes de la solution, montees sous le shell authentifie. */
  routes: SolutionRoute[]
  /** Rang d'affichage entre solutions. Defaut 100 ; croissant. */
  order?: number
  /**
   * Ecrans d'administration de la solution (Admin Pragmelis seul), ajoutes au menu « Administration »
   * apres les entrees de la plateforme (spec 148). La route correspondante est declaree dans `routes`,
   * et l'ecran porte lui-meme sa garde d'acces : le menu n'est qu'un gating d'affichage.
   */
  adminEntries?: NavItem[]
  /**
   * Compteurs affichés sur les entrees d'administration de la solution (spec 150, FR-020), par identifiant
   * d'entree. Hook React : appele a CHAQUE rendu de la barre laterale, `enabled` faux hors administrateur
   * (la solution ne doit alors rien lire). La plateforme ne sait ni ce qui est compte, ni pour quelle solution.
   */
  useAdminBadges?: (enabled: boolean) => Record<string, number>
  /**
   * Onglets de la fiche client (Admin Pragmelis), apres ceux de la plateforme (D-150-12). L'onglet porte sa propre
   * garde : la fiche n'est qu'un point d'affichage.
   */
  clientTabs?: SolutionClientTab[]
  /**
   * Onglets du hub « Clients » (Admin Pragmelis), apres ceux de la plateforme (D-150-12). Un compteur declare par
   * `useAdminBadges` sous l'identifiant de l'onglet s'affiche sur l'onglet ET se reporte sur l'entree « Clients ».
   */
  clientsHubTabs?: SolutionClientsHubTab[]
  /**
   * Spec 161 — section de la solution sur le Tableau de bord (une au plus). Elle montre ce qu'elle a pour le point de
   * vue reçu, ou rien. La plateforme l'affiche après le titre, le nom du client et la bascule, dans l'ordre du registre.
   */
  tableauDeBord?: ComponentType<SectionTableauDeBordProps>
  /**
   * Spec 161, second retour (2026-10-12) — actions de la solution dans le bandeau. Hook React : appelé à CHAQUE rendu du
   * bandeau, quel que soit `enabled` (la solution n'a rien à rendre quand il est faux, mais appelle ses hooks).
   */
  useActionsBandeau?: (params: ActionsBandeauParams) => ActionsBandeau
}

const registrations: SolutionRegistration[] = []

export function registerSolution(registration: SolutionRegistration): void {
  // Idempotent : le rechargement a chaud de Vite reevalue les modules, une double inscription
  // dupliquerait la section dans la barre laterale sans erreur visible ailleurs.
  const i = registrations.findIndex(r => r.code === registration.code)
  if (i >= 0) registrations[i] = registration
  else registrations.push(registration)
  registrations.sort((a, b) => (a.order ?? 100) - (b.order ?? 100) || a.code.localeCompare(b.code))
}

export function getRegisteredSolutions(): readonly SolutionRegistration[] {
  return registrations
}

export function getSolutionNavSections(): NavSection[] {
  return registrations.map(r => r.navSection)
}

export function getSolutionAdminEntries(): NavItem[] {
  return registrations.flatMap(r => r.adminEntries ?? [])
}

const noBadges = (): Record<string, number> => ({})

/**
 * Spec 150 — compteurs des entrees d'administration declares par les solutions, fusionnes. Les solutions sont
 * inscrites au chargement, avant le premier rendu : la liste des hooks appeles est donc stable d'un rendu a l'autre.
 */
export function useSolutionAdminBadges(enabled: boolean): Record<string, number> {
  const merged: Record<string, number> = {}
  for (const r of registrations) {
    const useBadges = r.useAdminBadges ?? noBadges
    // Boucle sur une liste FIGÉE au chargement (inscriptions avant le premier rendu) : le nombre et l'ordre des
    // hooks appelés sont identiques à chaque rendu, ce que la règle ne peut pas savoir.
    // eslint-disable-next-line react-hooks/rules-of-hooks -- liste des solutions stable d'un rendu à l'autre
    Object.assign(merged, useBadges(enabled))
  }
  return merged
}

const aucuneAction = (): ActionsBandeau => ({ items: [], element: null })

/**
 * Spec 161, second retour (2026-10-12) — actions du bandeau déclarées par les solutions, fusionnées dans l'ordre du
 * registre. Même garantie que `useSolutionAdminBadges` : la liste des solutions est figée au chargement, donc le nombre
 * et l'ordre des hooks appelés sont identiques d'un rendu à l'autre. Rien n'est proposé quand `enabled` est faux, même
 * si une solution l'oubliait.
 */
export function useSolutionActionsBandeau(params: ActionsBandeauParams): ActionsBandeau {
  const items: ActionBandeau[] = []
  const elements: ReactNode[] = []
  for (const r of registrations) {
    const useActions = r.useActionsBandeau ?? aucuneAction
    // eslint-disable-next-line react-hooks/rules-of-hooks -- liste des solutions stable d'un rendu à l'autre
    const { items: i, element } = useActions(params)
    items.push(...i)
    elements.push(createElement(Fragment, { key: r.code }, element))
  }
  return { items: params.enabled ? items : [], element: elements }
}

export function getSolutionClientTabs(): SolutionClientTab[] {
  return registrations.flatMap(r => r.clientTabs ?? [])
}

export function getSolutionClientsHubTabs(): SolutionClientsHubTab[] {
  return registrations.flatMap(r => r.clientsHubTabs ?? [])
}

/**
 * Entree du menu d'administration qui mene au hub « Clients ». Source unique : `navigation.ts` la lit ici, pour que
 * le report du compteur des onglets du hub ne depende pas d'une copie (revue T050).
 */
export const CLIENTS_HUB_ENTRY_ID = 'admin-clients'

/**
 * Compteur d'une entree du menu d'administration : celui que la solution declare pour elle, ou, pour l'entree
 * « Clients », la somme des compteurs des onglets de solution du hub (D-150-12).
 */
export function adminBadgeFor(itemId: string, badges: Record<string, number>): number {
  if (itemId === CLIENTS_HUB_ENTRY_ID) {
    return getSolutionClientsHubTabs().reduce((n, t) => n + (badges[t.id] ?? 0), 0)
  }
  return badges[itemId] ?? 0
}

/** Spec 161 — sections du Tableau de bord, dans l'ordre du registre ; les solutions sans section n'y figurent pas. */
export function getSectionsTableauDeBord(): { code: string; Section: ComponentType<SectionTableauDeBordProps> }[] {
  return registrations.flatMap(r => (r.tableauDeBord ? [{ code: r.code, Section: r.tableauDeBord }] : []))
}

export function getSolutionRoutes(): SolutionRoute[] {
  return registrations.flatMap(r => r.routes)
}
